import {BruteForce} from "../BruteForce";
import {SimpleReelModelSymbolPosition} from "../../core/reel/simplereel/SimpleReelModel";
import {gsap} from "gsap";
import {BruteForceHelper} from "../utils/BruteForceHelper";

export enum BruteForceSymbolStates {
    NORMAL,
    SPIN,
    LANDING,
    WIN,
    NO_WIN,
    NUDGE,
    SAD,
    HAPPY,
    IDLE
}

export class BruteForceSymbol extends PIXI.Container {
    protected _spine: PIXI.spine.Spine;
    private _spinBG: PIXI.Sprite;

    constructor(symbolName: string, symPos: SimpleReelModelSymbolPosition, initState: BruteForceSymbolStates) {
        super();
        this._symbolName = symbolName;
        this._reelId = symPos.reelID;
        this._symId = symPos.position;

        this.addSpinBG();
        this.addSpine();
        this.setFirstState(initState);


    }

    private _symbolName: string;

    get symbolName(): string {
        return this._symbolName;
    }

    private _reelId: number;

    get reelId(): number {
        return this._reelId;
    }

    private _symId: number;

    get symId(): number {
        return this._symId;
    }

    private _currentState: string;

    get currentState(): string {
        return this._currentState;
    }

    get currentStateName(): string {
        return this._currentState;
    }

    private setFirstState(state: BruteForceSymbolStates): void {
        switch (state) {
            case BruteForceSymbolStates.SPIN:
                this._spine.tint = 0xffffff
                this._spine.state.setAnimation(0, this._symbolName === "SC" ? "normal" : "spin", false);
                this._currentState = "spin";
                if (this._symbolName.includes("L")) {
                    this._spinBG.visible = true;
                    this._spinBG.alpha = 0.5;
                }
                break;
            default:
                this._spine.state.setAnimation(0, "normal", false);
                this._currentState = "normal";

        }
    }

    public setState(state: BruteForceSymbolStates): gsap.core.Timeline {
        const stateTl: gsap.core.Timeline = gsap.timeline();
        switch (state) {
            case BruteForceSymbolStates.SPIN:
                stateTl.add(() => {
                    this._spine.tint = 0xffffff
                }, 0);
                stateTl.add(BruteForceHelper.playSpineWithTimeline(this._spine, 0, this._symbolName === "SC" ? "normal" : "spin"), 0);
                this._currentState = "spin";
                if (this._symbolName.includes("L")) {
                    this._spinBG.visible = true;
                    this._spinBG.alpha = 0.5;
                }
                break;
            case BruteForceSymbolStates.LANDING:
                stateTl.add(() => {
                    this._spine.tint = 0xffffff;
                }, 0)
                stateTl.add(BruteForceHelper.playSpineWithTimeline(this._spine, 0, "landing"), 0);
                this._currentState = "landing";
                break;
            case BruteForceSymbolStates.WIN:
                if (this._symbolName.includes("XN")) {
                    stateTl.add(() => {
                        this._spine.tint = 0xffffff
                    }, 0);
                    if (this.currentState !== "happy") {
                        stateTl.add(BruteForceHelper.playSpineWithTimeline(this._spine, 0, "normal"), 0);
                        this._currentState = "win";
                    }
                } else {
                    stateTl.add(() => {
                        this._spine.tint = 0xffffff
                    }, 0);
                    stateTl.add(BruteForceHelper.playSpineWithTimeline(this._spine, 0, "win"), 0);
                    this._currentState = "win";
                }
                break;
            case BruteForceSymbolStates.NO_WIN:
                stateTl.add(() => {
                    this._spine.tint = 0x666666
                }, 0);
                if (this._symbolName.includes("XN")) {
                    if (this.currentState !== "happy") {
                        stateTl.add(BruteForceHelper.playSpineWithTimeline(this._spine, 0, "normal"), 0);
                        this._currentState = "nowin";
                    }
                } else {
                    stateTl.add(BruteForceHelper.playSpineWithTimeline(this._spine, 0, "normal"), 0);
                    this._currentState = "nowin";
                }
                break;
            case BruteForceSymbolStates.HAPPY:
                stateTl.add(() => {
                    this._spine.tint = 0xffffff;
                }, 0);
                stateTl.add(BruteForceHelper.playSpineWithTimeline(this._spine, 0, "happy"), 0);
                this._currentState = "happy";

                break;
            case BruteForceSymbolStates.SAD:
                stateTl.add(() => {
                    this._spine.tint = 0xffffff;
                }, 0);
                stateTl.add(BruteForceHelper.playSpineWithTimeline(this._spine, 0, "sad"), 0);
                this._currentState = "sad";

                break;
            case BruteForceSymbolStates.IDLE:
                stateTl.add(() => {
                    this._spine.tint = 0xffffff;
                }, 0);
                stateTl.add(BruteForceHelper.playSpineWithTimeline(this._spine, 0, "idle"), 0);
                this._currentState = "idle";

                break;
            default:
                stateTl.add(() => {
                    this._spine.tint = 0xffffff;
                }, 0);
                if (this.symbolName.includes("XN")) {
                    if (this.currentState !== "happy" && this.currentState !== "sad") {
                        stateTl.add(BruteForceHelper.playSpineWithTimeline(this._spine, 0, "normal"), 0);
                        this._currentState = "normal";
                    }
                } else {
                    stateTl.add(BruteForceHelper.playSpineWithTimeline(this._spine, 0, "normal"), 0);
                    this._currentState = "normal";

                }
        }
        return stateTl;
    }

    public setInteractive(val: boolean): void {
        if (["SC", "XN_1", "XN_2", "XN_3"].indexOf(this._symbolName) > -1) {
            return;
        }
        this.setInteractiveOnItem(val, this._spine);
    }

    public resetSpine(): void {
        BruteForceHelper.resetSpineItem(this._spine);
    }

    private addSpinBG(): void {
        this._spinBG = new PIXI.Sprite(BruteForce.assetLoader.getTextures("spinBG")[0]);
        this._spinBG.anchor.set(0.5, 0.5);
        this._spinBG.position.set(0, 0);
        this.addChild(this._spinBG);
        this._spinBG.visible = false;
    }

    private addSpine(): void {
        this._spine = new PIXI.spine.Spine(BruteForce.assetLoader.getSpineAsset(this._symbolName));
        if (this.symbolName.includes("L")) {
            this._spine.scale.set(0.8, 0.8);
        }
        this.addChild(this._spine);
    }

    private setInteractiveOnItem(val: boolean, item: PIXI.spine.Spine): void {
        item.interactive = val;
        item.buttonMode = val;
        if (val) {
            item.on("pointertap", () => BruteForce.miniPayTable.onClicked({
                dispObj: item,
                name: this._symbolName,
            }));
        } else {
            item.off("pointertap");
        }
    }

    public setNormal():void{
        this._spine.state.setAnimation(0,"normal",false);
        this._currentState = "normal";
    }

}